import React, { useState, useEffect } from 'react';
import 'twin.macro';
import { Eleve } from 'types/eleves';
import { Checkboxes } from '@teetsh/app/src/components/Form/Checkboxes';
import Button from 'components/Buttons/Button';
import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import InputGroup from 'components/Form/InputGroup';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import DeleteButton from 'components/Buttons/DeleteButton';

interface Props {
  eleves: Eleve[];
  selectedEleveIds: string[];
  onSubmit: (selectedIds: string[]) => void;
  onClose: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
}

export function IndividualElevesPanel(props: Props) {
  const { eleves, selectedEleveIds, onSubmit, onClose, onDelete, isEditing } =
    props;
  const { t } = useTranslation(['cahierJournal', 'eleve', 'common']);
  const [selected, setSelected] = useState<string[]>(selectedEleveIds);
  const [searchTerm, setSearchTerm] = useState('');

  // Detect when selectedEleveIds prop changes
  useEffect(() => {
    // Only update if actually different to prevent infinite loops
    if (JSON.stringify(selected) !== JSON.stringify(selectedEleveIds)) {
      setSelected(selectedEleveIds);
    }
  }, [selectedEleveIds]);

  const handleSubmit = () => {
    onSubmit(selected);
    onClose();
  };

  const filteredEleves = eleves.filter(eleve => {
    const fullName = `${eleve.givenName} ${eleve.familyName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  return (
    <div tw="space-y-4 pt-4">
      <div tw="space-y-3">
        <div tw="flex items-center justify-between">
          <h3 tw="text-sm font-semibold">
            {t('cahierJournal:ateliers.individualEleves.selectStudents')}
          </h3>
          <span tw="text-xs text-neutral-moderate">
            {t('eleve:xEleve', { count: selected.length })}
          </span>
        </div>

        {eleves.length > 10 && (
          <InputGroup
            id="search-eleves"
            type="text"
            placeholder={t(
              'cahierJournal:ateliers.individualEleves.searchPlaceholder',
            )}
            value={searchTerm}
            onChange={setSearchTerm}
            icon={MagnifyingGlassIcon}
          />
        )}

        {filteredEleves.length > 0 ? (
          <div>
            <Checkboxes
              options={filteredEleves.map(eleve => ({
                label: `${eleve.givenName} ${eleve.familyName}`,
                value: eleve.id,
              }))}
              value={selected}
              onChange={setSelected}
              condensed
            />
          </div>
        ) : searchTerm ? (
          <div tw="text-sm text-neutral-moderate italic py-4 text-center">
            {t('cahierJournal:ateliers.individualEleves.noResults')}
          </div>
        ) : (
          <div tw="text-sm text-neutral-moderate italic py-4 text-center">
            {t('cahierJournal:ateliers.individualEleves.noEleves')}
          </div>
        )}
      </div>

      <div tw="py-2 space-x-3 items-center flex justify-end sticky bottom-0 border-t -mx-4 px-4 bg-neutral-supershy">
        {isEditing && onDelete && (
          <DeleteButton
            withConfirmation
            intent="danger"
            onClick={onDelete}
            minimal
            small
            iconSize="sm"
            icon={TrashIcon}
          />
        )}
        <Button onClick={onClose} small>
          {t('common:buttons.cancel')}
        </Button>
        <Button intent="primary" onClick={handleSubmit} small icon={CheckIcon}>
          {t('common:buttons.validate')}
        </Button>
      </div>
    </div>
  );
}
